import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';

export interface PaymentRequestRecord {
  id: string;
  orgId: string;
  quoteId: string;
  amountCents: number;
  channels: ('sms' | 'email')[];
  methodsIncluded: string[];
  stripePaymentUrl?: string;
  sentBy: string;
  sentByName: string;
  sentAt: string;
}

interface PaymentRequestListResponse {
  success: boolean;
  data: PaymentRequestRecord[];
}

interface PaymentRequestSendResponse {
  success: boolean;
  data: PaymentRequestRecord;
  error?: string;
}

export function usePaymentRequest(orgId: string | null, quoteId: string | null) {
  const [history, setHistory] = useState<PaymentRequestRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchHistory = useCallback(async () => {
    if (!orgId || !quoteId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<PaymentRequestListResponse>(`/jobs/${orgId}/${quoteId}/payment-requests`);
      setHistory(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load payment requests');
    } finally {
      setLoading(false);
    }
  }, [orgId, quoteId]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const sendRequest = useCallback(
    async (channels: ('sms' | 'email')[]) => {
      if (!orgId || !quoteId) throw new Error('No job loaded');
      setSending(true);
      setError(null);
      try {
        const paymentUrl = `${window.location.origin}/pay/${orgId}/${quoteId}`;
        const res = await api.post<PaymentRequestSendResponse>(
          `/jobs/${orgId}/${quoteId}/request-payment`,
          { channels, paymentUrl },
        );
        // Prepend the new record to history
        setHistory(prev => [res.data, ...prev]);
        return res.data;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to send payment request';
        setError(message);
        throw err;
      } finally {
        setSending(false);
      }
    },
    [orgId, quoteId],
  );

  return { history, loading, sending, error, sendRequest, refetch: fetchHistory };
}
